import React from 'react';
import PropTypes from 'prop-types';

import Image from '../ui/Image';
import { Grid, Row, Col } from '../ui/Grid';
import DropdownButton from '../ui/DropdownButton';
import MenuItem from '../ui/MenuItem';

import { STATIC_HOST_URL } from '../../settings';
import { DKD, COVID_AKI, DKD_INDUCED } from './data';

const rootStyle = {
  paddingLeft: 0,
  paddingRight: 0,
  marginBottom: '50px',
};

const bannerStyle = {
  backgroundImage: `url('net_banner_full.svg')`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center 0',
  backgroundSize: 'auto 460px',
  marginTop: '30px',
  paddingBottom: '30px',
  paddingTop: '30px',
  height: '200px',
  textAlign: 'center',
  width: '90%',
  marginLeft: 'auto',
  marginRight: 'auto',
};

const resultStyle = {
  marginBottom: '20px',
  textAlign: 'center',
};

const imageContainerStyle = {
  minHeight: '500px',
  marginTop: '10px',
};

const imageStyle = {
  textAlign: 'center',
  marginTop: '20px',
  display: 'block',
  marginLeft: 'auto',
  marginRight: 'auto',
};

const legendContainerStyle = {
  textAlign: 'center',
  marginTop: '20px',
};

const legendParagraphStyle = {
  paddingTop: '10px',
  paddingRight: '100px',
  paddingBottom: '20px',
  paddingLeft: '100px',
};

const h2Style = {
  textAlign: 'center',
};

// --- Component Definitions ---

const DKD_FILES = [
  { file: 'dkd_genes.txt', name: 'DKD genes' },
  {
    file: 'dkd_modules.tsv',
    name: 'DKD modules',
  },
];
const COVID_AKI_FILES = [
  { file: 'covid-aki_genes.txt', name: 'COVID AKI genes' },
  {
    file: 'covid-aki_modules.tsv',
    name: 'COVID AKI modules',
  },
];

const DKD_INDUCED_FILES = [
  { file: 'dkd-induced_genes.txt', name: 'DKD-induced genes' },
  {
    file: 'dkd-induced_modules.tsv',
    name: 'DKD-induced modules',
  },
];

const DownloadButton = ({ files }) => {
  const items = files.map((file, idx) => (
    <MenuItem
      eventKey={idx + 1}
      href={`${STATIC_HOST_URL}covid19/${file.file}`}
      key={idx}
    >
      {file.name}
    </MenuItem>
  ));
  return (
    <DropdownButton
      title={
        <span>
          {`Download `}
          <i className="fa fa-download fa-fw" aria-hidden="true" />
        </span>
      }
      style={{ marginLeft: 10 }}
      id="download-button"
    >
      {items}
    </DropdownButton>
  );
};

DownloadButton.propTypes = {
  files: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const CovidPage = () => {
  const ModuleResult = ({ filesList, imageURL, legendText }) => (
    <div>
      <DownloadButton files={filesList} />
      <div style={imageContainerStyle}>
        <Image src={imageURL} responsive style={imageStyle} />
      </div>
      <div style={legendContainerStyle}>
        <p style={legendParagraphStyle}>{legendText}</p>
      </div>
    </div>
  );

  ModuleResult.propTypes = {
    filesList: PropTypes.arrayOf(PropTypes.object).isRequired,
    imageURL: PropTypes.string.isRequired,
    legendText: PropTypes.oneOfType([PropTypes.element, PropTypes.string])
      .isRequired,
  };

  return (
    <div style={rootStyle}>
      <Grid fluid={false}>
        <Row style={bannerStyle}>
          <Col>
            <h1>
              SARS-CoV-2 receptor networks in diabetic and COVID-19 associated
              kidney disease
            </h1>
            <p
              style={{
                width: '70%',
                margin: 'auto',
                marginBottom: '20px',
                fontSize: '21px',
                fontWeight: 300,
                lineHeight: 1.4,
              }}
            >
              {` ACE2+ gene expression signatures in diabetic kidney disease (DKD) and COVID-19 patients in proximal tubule cells (PTEC) `}
              <br />
              {`[`}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.kidney-international.org/article/S0085-2538(20)31113-3/fulltext"
              >
                Published in Kidney International
              </a>
              {`]`}
            </p>
          </Col>
        </Row>
        <Row>
          <Col>
            <hr />
          </Col>
        </Row>
        <Row>
          <Col>
            <h2 style={h2Style}>
              {`DKD ACE2+ expression signature (${DKD.length} genes)`}
            </h2>
          </Col>
        </Row>
        <Row style={resultStyle}>
          <Col md={12}>
            <ModuleResult
              filesList={DKD_FILES}
              imageURL={`${STATIC_HOST_URL}covid19/dkd.svg`}
              legendText="The 9 functional modules characterizing the genes upregulated in ACE2+ cells in DKD (ACE2+ vs ACE2- in DKD)"
            />
            <hr />
          </Col>
        </Row>
        <Row>
          <Col>
            <h2 style={h2Style}>
              {`DKD-induced ACE2+ expression signature (${DKD_INDUCED.length} genes)`}
            </h2>
          </Col>
        </Row>
        <Row style={resultStyle}>
          <Col md={12}>
            <ModuleResult
              filesList={DKD_INDUCED_FILES}
              imageURL={`${STATIC_HOST_URL}covid19/dkd-induced.svg`}
              legendText="The 9 functional modules characterizing the genes upregulated in DKD ACE2+ cells (DKD ACE2+ vs living donor ACE2+)"
            />
            <hr />
          </Col>
        </Row>
        <Row>
          <Col>
            <h2 style={h2Style}>
              {`COVID ACE2+ expression signature (${COVID_AKI.length} genes)`}
            </h2>
          </Col>
        </Row>
        <Row style={resultStyle}>
          <Col md={12}>
            <ModuleResult
              filesList={COVID_AKI_FILES}
              imageURL={`${STATIC_HOST_URL}covid19/covid-aki.svg`}
              legendText="The 10 functional modules characterizing the genes upregulated in ACE2+ cells from the PTEC in urine of COVID patients (ACE2+ vs ACE2- in urine sample PTEC)"
            />
            <hr />
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

export default CovidPage;
